import { useCallback, useRef, useState } from "react";
import { Camera, Trash2, RotateCw, Loader2 } from "lucide-react";
import type { UploadedPhoto } from "./PhotoCapture";
import { cn } from "@/lib/utils";

interface SinglePhotoSlotProps {
  label: string;
  photo: UploadedPhoto | null;
  onCapture: (base64: string) => Promise<void>;
  onDelete: (photoId: string) => Promise<void>;
  disabled?: boolean;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read file"));
        return;
      }
      resolve(result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader error"));
    reader.readAsDataURL(file);
  });
}

type SlotState = "idle" | "uploading" | "failed";

export function SinglePhotoSlot({
  label,
  photo,
  onCapture,
  onDelete,
  disabled,
}: SinglePhotoSlotProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<SlotState>("idle");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const lastBase64Ref = useRef<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      const base64 = await fileToBase64(file);
      lastBase64Ref.current = base64;
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
      setState("uploading");
      try {
        await onCapture(base64);
        setState("idle");
        setPreviewUrl(null);
        URL.revokeObjectURL(preview);
      } catch {
        setState("failed");
      }
    },
    [onCapture],
  );

  const handleRetry = useCallback(async () => {
    if (!lastBase64Ref.current) return;
    setState("uploading");
    try {
      await onCapture(lastBase64Ref.current);
      setState("idle");
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    } catch {
      setState("failed");
    }
  }, [onCapture, previewUrl]);

  const handleCancel = useCallback(() => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setState("idle");
    lastBase64Ref.current = null;
  }, [previewUrl]);

  if (state === "uploading" && previewUrl) {
    return (
      <div className="space-y-1.5">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <div className="relative aspect-[4/3] overflow-hidden rounded-md border bg-muted">
          <img src={previewUrl} alt="Uploading" className="h-full w-full object-cover opacity-60" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/40 text-white">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-[10px] font-medium">Uploading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (state === "failed" && previewUrl) {
    return (
      <div className="space-y-1.5">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <div className="relative aspect-[4/3] overflow-hidden rounded-md border bg-muted">
          <img src={previewUrl} alt="Failed" className="h-full w-full object-cover opacity-60" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/40 text-white">
            <span className="text-[10px] font-medium">Failed</span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={handleRetry}
                className="inline-flex h-8 min-w-11 items-center gap-1 rounded-md bg-white px-2 text-[11px] font-medium text-black active:bg-white/80"
              >
                <RotateCw className="h-3 w-3" />
                Retry
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex h-8 items-center rounded-md bg-black/60 px-2 text-[11px] font-medium text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (photo) {
    return (
      <div className="space-y-1.5">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        <div className="relative aspect-[4/3] overflow-hidden rounded-md border bg-muted">
          <img src={photo.url} alt={label} className="h-full w-full object-cover" loading="lazy" />
          {!disabled && (
            <button
              type="button"
              onClick={() => onDelete(photo.id)}
              className="absolute top-1 right-1 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white active:bg-black/80"
              aria-label={`Delete ${label}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
        {!disabled && (
          <>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-xs text-primary font-medium"
            >
              Retake
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onDelete(photo.id).then(() => handleFile(file));
                }
                e.target.value = "";
              }}
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className={cn(
          "flex w-full aspect-[4/3] flex-col items-center justify-center gap-1 rounded-md",
          "border-2 border-dashed border-muted-foreground/40 bg-muted/20",
          "text-muted-foreground active:bg-muted/40 disabled:opacity-50",
        )}
        aria-label={`Capture ${label}`}
      >
        <Camera className="h-6 w-6" />
        <span className="text-xs font-medium">Take photo</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}
